import { useContext } from 'react'
import { Center, NumberFormatter, Stack, Table, Text, Title } from '@mantine/core'
import classes from './lot-data-print-table.module.css'
import { LotDetail } from '@loom-api-types'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { StationContext } from '@/pages/stations/station-context'

export interface LotDataPrintTableProps {
  data: LotDetail[]
  sumOfTotalBales?: number
  sumOfTotalNetKg?: number
  weightedAverageOfMicron?: number
  weightedAverageOfVegetableMatter?: number
  weightedAverageOfYield?: number
  weightedAverageOfColour?: number
  weightedAverageOfStapleLength?: number
  weightedAverageOfStapleStrength?: number
  weightedAverageOfStapleAverageStrength?: number
  weightedAverageOfPointOfBreakTip?: number
  weightedAverageOfPointOfBreakMid?: number
  weightedAverageOfPointOfBreakBase?: number
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  season?: string
}

export const LotDataPrintTable = ({
  data,
  sumOfTotalBales,
  sumOfTotalNetKg,
  weightedAverageOfMicron,
  weightedAverageOfVegetableMatter,
  weightedAverageOfYield,
  weightedAverageOfColour,
  weightedAverageOfStapleLength,
  weightedAverageOfStapleStrength,
  weightedAverageOfStapleAverageStrength,
  weightedAverageOfPointOfBreakTip,
  weightedAverageOfPointOfBreakMid,
  weightedAverageOfPointOfBreakBase,
  noDataMessage,
  isLoading,
  isError,
  season,
}: LotDataPrintTableProps) => {
  const stationContext = useContext(StationContext)

  return (
    <Stack gap='sm'>
      <Text size='sm'>
        NZM Wool Data Report - {season ? `${season} season` : 'All unsold lots'}
      </Text>
      <Title order={1} size='h3'>
        {stationContext?.brand}{' '}
        <Text component='span' c='gray.5' inherit>
          {stationContext?.recordNumber}
        </Text>
      </Title>
      {isLoading && (
        <Center>
          <Text>Loading...</Text>
        </Center>
      )}
      {isError && (
        <Center>
          <Text c='red'>Error loading data</Text>
        </Center>
      )}
      {!isLoading && !isError && data.length === 0 && (
        <Center>
          <Text>{noDataMessage || 'No data available'}</Text>
        </Center>
      )}
      {!isLoading && !isError && data.length > 0 && (
        <Table verticalSpacing='xs'>
          <Table.Thead>
            <Table.Tr className='topHeaderRow'>
              <Table.Th rowSpan={2}>SALE</Table.Th>
              <Table.Th rowSpan={2}>LINE NUM</Table.Th>
              <Table.Th rowSpan={2}>REF</Table.Th>
              <Table.Th rowSpan={2}>DESCRIPTION</Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                BALES
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                NET KG
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                MIC
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                VM
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                YLD
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                COLOUR Y-Z
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                SL
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                SS
              </Table.Th>
              <Table.Th rowSpan={2} className={classes.alignRight}>
                SS25
              </Table.Th>
              <Table.Th colSpan={3} className={classes.alignCenter}>
                POINT OF BREAK
              </Table.Th>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.alignCenter}>T</Table.Th>
              <Table.Th className={classes.alignCenter}>M</Table.Th>
              <Table.Th className={classes.alignCenter}>B</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((lot, lotIndex) => (
              <Table.Tr
                key={`lot-${lotIndex}`}
                className={lotIndex % 2 === 0 ? classes.oddRow : ''}
              >
                <Table.Td>{lot.saleAndSeason}</Table.Td>
                <Table.Td>{lot.lineNumber}</Table.Td>
                <Table.Td>{lot.referenceNumber}</Table.Td>
                <Table.Td>{lot.description}</Table.Td>
                <Table.Td className={classes.alignRight}>{lot.bales}</Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter value={lot.netKgs ?? ''} thousandSeparator />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestMicron ?? ''}
                    thousandSeparator
                    decimalScale={1}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestVegetableMatterBase ?? ''}
                    thousandSeparator
                    decimalScale={1}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestYield ?? ''}
                    thousandSeparator
                    decimalScale={1}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestColourYZ ?? ''}
                    thousandSeparator
                    decimalScale={1}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestStapleLengthGreasy ?? ''}
                    thousandSeparator
                    decimalScale={0}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestStapleStrength ?? ''}
                    thousandSeparator
                    decimalScale={0}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter
                    value={lot.coreTestStapleAverageStrength ?? ''}
                    decimalScale={0}
                  />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter value={lot.coreTestPointOfBreakTip ?? ''} decimalScale={0} />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter value={lot.coreTestPointOfBreakMid ?? ''} decimalScale={0} />
                </Table.Td>
                <Table.Td className={classes.alignRight}>
                  <NumberFormatter value={lot.coreTestPointOfBreakBase ?? ''} decimalScale={0} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={4} className={classes.alignRight}>
                <Text size='sm' fw={700}>
                  TOTALS & AVERAGES
                </Text>
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter value={sumOfTotalBales ?? ''} thousandSeparator />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter value={sumOfTotalNetKg ?? ''} thousandSeparator />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfMicron ?? ''}
                  thousandSeparator
                  decimalScale={1}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfVegetableMatter ?? ''}
                  thousandSeparator
                  decimalScale={1}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfYield ?? ''}
                  thousandSeparator
                  decimalScale={1}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfColour ?? ''}
                  thousandSeparator
                  decimalScale={1}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfStapleLength ?? ''}
                  thousandSeparator
                  decimalScale={0}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfStapleStrength ?? ''}
                  thousandSeparator
                  decimalScale={0}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfStapleAverageStrength ?? ''}
                  thousandSeparator
                  decimalScale={0}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfPointOfBreakTip ?? ''}
                  thousandSeparator
                  decimalScale={0}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfPointOfBreakMid ?? ''}
                  thousandSeparator
                  decimalScale={0}
                />
              </Table.Td>
              <Table.Td className={classes.alignRight}>
                <NumberFormatter
                  value={weightedAverageOfPointOfBreakBase ?? ''}
                  thousandSeparator
                  decimalScale={0}
                />
              </Table.Td>
            </Table.Tr>
            <Table.Tr style={{ border: 'none', paddingRight: '0' }}>
              <Table.Td
                colSpan={16}
                style={{ border: 'none', backgroundColor: 'transparent', paddingRight: '0' }}
                className={classes.alignRight}
              >
                Data generated {formatDate(new Date(), 'DD/MM/YYYY h:mm A')}
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      )}
    </Stack>
  )
}
